/**
 * useAuth — session state hook.
 *
 * Fetches the current user on mount via GET /api/v1/auth/me.
 * Exposes login, signup, and logout actions that update local state.
 *
 * Usage:
 *   const { user, loading, login, signup, logout } = useAuth();
 *
 * Note: This hook is intended to be used with a context provider (AuthContext)
 * so that user state is shared across the component tree. For v1 the state
 * is fetched once on mount; a future iteration can add polling or SSE.
 */

import { useState, useEffect, useCallback } from 'react';
import * as authApi from '../api/auth';
import type { AuthUser } from '../api/auth';

export interface UseAuthResult {
  /** The currently authenticated user, or null if not logged in. */
  user: AuthUser | null;
  /** True while the initial session check is in progress. */
  loading: boolean;
  /** Log in with email + password. Throws AuthApiError on failure. */
  login: (email: string, password: string) => Promise<void>;
  /** Create an account. Throws AuthApiError on failure. */
  signup: (email: string, password: string) => Promise<void>;
  /** Log out the current user. */
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Check the current session on mount.
  useEffect(() => {
    authApi
      .getMe()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const authUser = await authApi.login(email, password);
    setUser(authUser);
  }, []);

  const signup = useCallback(async (email: string, password: string) => {
    const authUser = await authApi.signup(email, password);
    setUser(authUser);
  }, []);

  const logoutFn = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  return { user, loading, login, signup, logout: logoutFn };
}
